import { el, list, setAttr } from 'redom';
import { debounce } from 'lodash';

import { stringToSVG } from 'utilities';

import OutsideClickEventRegistration from 'lib/outside-click-event-registration';

import './index.scss';

import clearInputButtonIconString from 'assets/images/clear-input-button-icon.svg?raw';

const clearInputButtonIcon = stringToSVG(clearInputButtonIconString);

export const filterTypes = {
  GRAYSCALE: 'GRAYSCALE',
  SEPIA: 'SEPIA',
  INVERT: 'INVERT'
};

const filterButtons = [{
  type: filterTypes.GRAYSCALE,
  label: 'Grayscale'
}, {
  type: filterTypes.SEPIA,
  label: 'Sepia'
}, {
  type: filterTypes.INVERT,
  label: 'Invert'
}];

class FilterButton {

  constructor(filterPanelContent) {

    this.filterPanelContent = filterPanelContent;
    this.filter = null;

    this.brightnessInput = null;
    this.contrastInput = null;
    this.saturationInput = null;
    this.hueInput = null;
    this.blurInput = null;
    this.opacityInput = null;
    this.pixelateInput = null;

    this.el = el('button.filter-type-control', {
      type: 'button',
      onclick: this.handleFilterTypeControlClick.bind(this)
    });

  }

  handleFilterTypeControlClick(event) {

    setAttr(this.el, { disabled: true });

    event.preventDefault();

    this.filterPanelContent.handleFilterTypeControlClick(event, this.filter.type);

    setAttr(this.el, { disabled: false });

  }

  update(filter) {

    this.filter = filter;

    this.el.textContent = this.filter.label;

    if (this.filterPanelContent.isFilterTypeActive(this.filter.type) === true) {
      this.el.classList.add('active');
    } else {
      this.el.classList.remove('active');
    }

  }

}

class FilterPanelContent {

  constructor(configuration) {

    this.onClose = configuration.onClose;
    this.configuration = configuration.filter;
    this.configuration.brightness = this.configuration.brightness || 100;
    this.configuration.contrast = this.configuration.contrast || 100;
    this.configuration.saturation = this.configuration.saturation || 100;
    this.configuration.hue = this.configuration.hue || 0;
    this.configuration.blur = this.configuration.blur || 0;
    this.configuration.opacity = this.configuration.opacity || 100;
    this.configuration.pixelate = this.configuration.pixelate || 0;
    this.outsideEventRegistration = null;

    this.filterTypeButtons = list('.filter-type-controls', FilterButton, 'type', this);

    this.filterTypeButtons.update(filterButtons);

    this.el = el('.filter-panel-content-main', [
      this.filterTypeButtons,
      this.renderBrightnessControl(),
      this.renderContrastControl(),
      this.renderSaturationControl(),
      this.renderHueControl(),
      this.renderBlurControl(),
      this.renderOpacityControl(),
      this.renderPixelateControl(),
      this.renderRemoveAllControl()
    ]);

  }

  isFilterTypeActive(filterType) {

    return this.configuration.filterType === filterType;

  }

  handleFilterTypeControlClick(event, filterType) {

    if (this.configuration.filterType === filterType) {
      this.configuration.filterType = null;
      this.configuration.onTypeChange(filterType, false);
    } else {
      this.configuration.filterType = filterType;
      this.configuration.onTypeChange(filterType, true);
    }

    this.filterTypeButtons.update(filterButtons);

  }

  handleBrightnessControlInput(event) {

    this.configuration.brightness = event.target.value;

    this.configuration.onBrightnessChange(this.configuration.brightness);

  }

  renderBrightnessControl() {

    return el('.filter-control', [
      el('label', 'Brightness'),
      this.brightnessInput = el('input', {
        type: 'range',
        min: 0,
        max: 100,
        value: this.configuration.brightness,
        oninput: debounce(this.handleBrightnessControlInput.bind(this), 100)
      })
    ]);

  }

  handleContrastControlInput(event) {

    this.configuration.contrast = event.target.value;

    this.configuration.onContrastChange(this.configuration.contrast);

  }

  renderContrastControl() {

    return el('.filter-control', [
      el('label', 'Contrast'),
      this.contrastInput = el('input', {
        type: 'range',
        min: 0,
        max: 100,
        value: this.configuration.contrast,
        oninput: debounce(this.handleContrastControlInput.bind(this), 100)
      })
    ]);

  }

  handleSaturationControlInput(event) {

    this.configuration.saturation = event.target.value;

    this.configuration.onSaturationChange(this.configuration.saturation);

  }

  renderSaturationControl() {

    return el('.filter-control', [
      el('label', 'Saturation'),
      this.saturationInput = el('input', {
        type: 'range',
        min: 0,
        max: 100,
        value: this.configuration.saturation,
        oninput: debounce(this.handleSaturationControlInput.bind(this), 100)
      })
    ]);

  }

  handleHueControlInput(event) {

    this.configuration.hue = event.target.value;

    this.configuration.onHueChange(this.configuration.hue);

  }

  renderHueControl() {

    return el('.filter-control', [
      el('label', 'Hue'),
      this.hueInput = el('input', {
        type: 'range',
        min: 0,
        max: 360,
        value: this.configuration.hue,
        oninput: debounce(this.handleHueControlInput.bind(this), 100)
      })
    ]);

  }

  handleBlurControlInput(event) {

    this.configuration.blur = event.target.value;

    this.configuration.onBlurChange(this.configuration.blur);

  }

  renderBlurControl() {

    return el('.filter-control', [
      el('label', 'Blur'),
      this.blurInput = el('input', {
        type: 'range',
        min: 0,
        max: 100,
        value: this.configuration.blur,
        oninput: debounce(this.handleBlurControlInput.bind(this), 100)
      })
    ]);

  }

  handleOpacityControlInput(event) {

    this.configuration.opacity = event.target.value;

    this.configuration.onOpacityChange(this.configuration.opacity);

  }

  renderOpacityControl() {

    return el('.filter-control', [
      el('label', 'Opacity'),
      this.opacityInput = el('input', {
        type: 'range',
        min: 0,
        max: 100,
        value: this.configuration.opacity,
        oninput: debounce(this.handleOpacityControlInput.bind(this), 100)
      })
    ]);

  }

  handlePixelateControlInput(event) {

    this.configuration.pixelate = event.target.value;

    this.configuration.onPixelationChange(this.configuration.pixelate);

  }

  renderPixelateControl() {

    return el('.filter-control', [
      el('label', 'Pixelate'),
      this.pixelateInput = el('input', {
        type: 'range',
        min: 0,
        max: 100,
        value: this.configuration.pixelate,
        oninput: debounce(this.handlePixelateControlInput.bind(this), 100)
      })
    ]);

  }

  handleRemoveAllControlClick(event) {

    this.configuration.filterType = null;
    this.configuration.brightness = 100;
    this.configuration.contrast = 100;
    this.configuration.saturation = 100;
    this.configuration.hue = 0;
    this.configuration.blur = 0;
    this.configuration.opacity = 100;
    this.configuration.pixelate = 0;

    this.brightnessInput.value = this.configuration.brightness;
    this.contrastInput.value = this.configuration.contrast;
    this.saturationInput.value = this.configuration.saturation;
    this.hueInput.value = this.configuration.hue;
    this.blurInput.value = this.configuration.blur;
    this.opacityInput.value = this.configuration.opacity;
    this.pixelateInput.value = this.configuration.pixelate;

    this.configuration.onTypeChange(null, false);
    this.configuration.onBrightnessChange(this.configuration.brightness);
    this.configuration.onContrastChange(this.configuration.contrast);
    this.configuration.onSaturationChange(this.configuration.saturation);
    this.configuration.onHueChange(this.configuration.hue);
    this.configuration.onBlurChange(this.configuration.blur);
    this.configuration.onOpacityChange(this.configuration.opacity);
    this.configuration.onPixelationChange(this.configuration.pixelate);

    this.filterTypeButtons.update(filterButtons);

  }

  renderRemoveAllControl() {

    return el('.filter-controls-footer', [
      el('button.filter-control', {
        onclick: this.handleRemoveAllControlClick.bind(this)
      }, [clearInputButtonIcon.cloneNode(true)])
    ]);

  }

  handleClose(target, event) {

    event.stopPropagation();

    this.onClose();

  }

  onmount() {

    this.outsideEventRegistration = new OutsideClickEventRegistration(this.el, this.handleClose.bind(this));

  }

  onunmount() {

    this.outsideEventRegistration.cancel();

  }

}

export default FilterPanelContent;